import { combine, createStore, createEvent, createEffect, sample } from 'effector'
import { pending } from 'patronum/pending'

import { goTo, urlTo } from 'helpers/router'

import { setup2fa, generateRecoveryCode, setup2faWithRecoveryCode } from 'app/effector/2fa/api'
import { AuthFactor } from 'app/effector/2fa/types'

import { fetchClient } from 'app/redux/actions/client'
import { showFailToast } from 'app/redux/actions/ui'
import store from 'app/redux/store/store.js'

import { Step, Flow, flows, stepModule } from './constants'

// Effects
const navigateToStepFx = createEffect((step: Step) => {
  goTo(urlTo(stepModule[step], {}, window.location.search))
})

const startTwoFactorSetupFx = createEffect(async ({ factor, token }: { factor: AuthFactor; token: string }) => {
  await setup2fa({ factor, token, action: 'enable', type: 'push_and_sms' })
})

const generateRecoveryCodeFx = createEffect(async () => {
  const result = await generateRecoveryCode()

  return result
})

const confirmRecoveryCodeFx = createEffect(async (id: number) => {
  const result = await setup2faWithRecoveryCode(id)

  return result
})

const showFailToastFx = createEffect(({ error }) => {
  showFailToast(error?.response?.data?.errors?.[0]?.message ?? 'Something went wrong')
})

const fetchClientFx = createEffect(() => {
  store.dispatch(fetchClient())
})

// Stores
const $step = createStore<Step>(Step.INITIAL)
const $flow = createStore<Flow>(Flow.PUSH_AND_CODE)
const $recoveryCode = createStore<{ raw: string; id: number | null }>({ raw: '', id: null })

// Events
const setStep = createEvent<Step>()
const setFlow = createEvent<Flow>()
const goBack = createEvent()
const goNext = createEvent()
const startFlow = createEvent<Flow>()
const saveRecoveryCode = createEvent()

// Store updates
$step.on(setStep, (state, value) => value)
$flow.on(setFlow, (state, value) => value)
$recoveryCode.on(generateRecoveryCodeFx.doneData, (state, data) => data)

sample({
  clock: goBack,
  source: [$step, $flow],
  fn: ([step, flow]) => {
    const currentFlow = flows[flow]
    const currentStepIndex = currentFlow.indexOf(step)

    return currentStepIndex <= 0 ? currentFlow[0] : currentFlow[currentStepIndex - 1]
  },
  target: setStep,
})

sample({
  clock: goNext,
  source: [$step, $flow],
  fn: ([step, flow]) => {
    const currentFlow = flows[flow]
    const currentStepIndex = currentFlow.indexOf(step)

    return currentFlow[currentStepIndex + 1]
  },
  target: setStep,
})

sample({
  clock: startFlow,
  target: [setFlow, goNext],
})

sample({
  clock: setStep,
  fn: (step) => step,
  target: navigateToStepFx,
})

sample({
  clock: [startTwoFactorSetupFx.done],
  fn: ({ params }) => {
    if (params.factor === AuthFactor.PUSH) return Flow.PUSH_AND_CODE
    if (params.factor === AuthFactor.SMS) return Flow.SMS_AND_CODE
  },
  target: startFlow,
})

sample({
  clock: [confirmRecoveryCodeFx.done],
  target: goNext,
})

sample({
  clock: saveRecoveryCode,
  source: $recoveryCode,
  fn: (recoveryCode) => recoveryCode.id,
  target: confirmRecoveryCodeFx,
})

sample({
  clock: [startTwoFactorSetupFx.fail, generateRecoveryCodeFx.fail, confirmRecoveryCodeFx.fail],
  target: showFailToastFx,
})

sample({
  clock: setStep,
  filter: (step) => step === Step.SUCCESS,
  target: fetchClientFx,
})

const $isLoading = pending([startTwoFactorSetupFx, generateRecoveryCodeFx, confirmRecoveryCodeFx])
const $currentStepNumber = combine($flow, $step, (flow, step) => {
  return flows[flow].indexOf(step) + 1
})
const $currentFlowStepsCount = $flow.map((flow) => flows[flow].length - 1)

export {
  $step,
  $flow,
  $isLoading,
  $recoveryCode,
  $currentStepNumber,
  $currentFlowStepsCount,
  goBack,
  goNext,
  startFlow,
  startTwoFactorSetupFx,
  showFailToastFx,
  generateRecoveryCodeFx,
  saveRecoveryCode,
  fetchClientFx,
}
